import { ArrowUpRight, Heart, Leaf, ShieldCheck, ListChecks } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { TrustBadges } from '../components/TrustBadges';
import { Watermark } from '../components/Watermark';
import { ABOUT_IMAGE, COMPANY, STATS } from '../lib/site';

const VALUES = [
    { icon: ShieldCheck, title: 'Trustworthy', text: 'Every cleaner is fully employed by us, reference-checked, and trained for 30+ hours before their first job.' },
    { icon: Heart, title: 'Considered', text: 'We treat your home the way we would treat ours — quietly, carefully, and with care for what matters.' },
    { icon: Leaf, title: 'Low-tox', text: 'Eco-friendly, low-fragrance products by default. Ask us about our hypoallergenic kit.' },
    { icon: ListChecks, title: 'Detail-led', text: 'Every job follows a 60-point checklist. Photo report after every visit, no exceptions.' },
];

const AboutPage = ({ onOpenQuote }) => {
    return (
        <main data-testid="about-page">
            <section className="pt-16 md:pt-24 pb-16 md:pb-24">
                <div className="container-axion grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <Reveal className="lg:col-span-7">
                        <div className="overline">Our story</div>
                        <h1 className="font-outfit font-medium tracking-tight mt-4 text-5xl md:text-6xl lg:text-[5rem] leading-[1.02]">
                            Cleaning,<br />
                            <em className="italic font-light text-accent">re-considered for Bristol.</em>
                        </h1>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-5 lg:pb-3">
                        <p className="text-muted-foreground text-lg leading-relaxed">
                            {COMPANY.legal} was founded by a team of former hospitality professionals who couldn't
                            understand why most cleaning companies still feel like they belong in 1995.
                            Quiet uniforms, careful tools, photo reports, and prices that make sense.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-24 md:pb-32">
                <div className="container-axion">
                    <Reveal>
                        <div className="relative rounded-2xl overflow-hidden">
                            <img src={ABOUT_IMAGE} alt="Axion team at work" className="w-full aspect-[16/8] object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 via-transparent to-transparent" />
                            <Watermark size="md" position="tr" />
                            <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 bg-white/90 backdrop-blur rounded-2xl p-6 max-w-md">
                                <div className="overline">Bristol HQ</div>
                                <p className="font-outfit text-xl mt-3 text-foreground leading-snug">
                                    "We started Axion because we wanted a cleaner we'd recommend to our own mothers."
                                </p>
                                <div className="mt-4 text-xs text-muted-foreground">— The founders</div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-muted">
                <div className="container-axion">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end mb-14">
                        <Reveal className="md:col-span-6">
                            <div className="overline">Values</div>
                            <h2 className="font-outfit font-medium tracking-tight mt-4 text-4xl md:text-5xl leading-[1.05]">
                                The four things we<br />never compromise on.
                            </h2>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={i * 90} className="bg-white border border-border rounded-2xl p-7">
                                <span className="grid place-items-center w-11 h-11 rounded-full bg-accent/15 text-primary">
                                    <v.icon className="w-5 h-5" strokeWidth={1.6} />
                                </span>
                                <h3 className="font-outfit text-lg mt-5">{v.title}</h3>
                                <p className="text-muted-foreground text-[14px] leading-relaxed mt-2">{v.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-24 bg-white border-y border-border">
                <div className="container-axion grid grid-cols-2 md:grid-cols-4 gap-10">
                    {STATS.map((s, i) => (
                        <Reveal key={s.label} delay={i * 80}>
                            <div className="font-outfit text-3xl md:text-4xl tracking-tight">{s.value}</div>
                            <div className="text-xs text-muted-foreground mt-2 tracking-wider uppercase">{s.label}</div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="py-20">
                <div className="container-axion">
                    <TrustBadges />
                </div>
            </section>

            <section className="py-20 md:py-24 bg-primary text-primary-foreground">
                <div className="container-axion grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-8">
                        <h2 className="font-outfit text-3xl md:text-5xl tracking-tight">Ready to meet your new cleaner?</h2>
                        <p className="text-white/70 mt-4">Free quote in under an hour. No high-pressure sales calls.</p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <button onClick={onOpenQuote} data-testid="about-cta-quote" className="inline-flex items-center gap-2 rounded-full bg-white text-primary px-7 py-4 text-sm font-medium font-outfit hover:-translate-y-0.5 transition-transform">
                            Get a Free Quote <ArrowUpRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default AboutPage;
